import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableCellHead,
  TableHead,
  TableRow,
} from '~/ui/Table';
import { useTranslation } from 'react-i18next';
import CustomCheckbox from '~/components/Shared/CustomCheckbox/CustomCheckbox';
import Tooltip from '~/components/Shared/Tooltip/Tooltip';
import InfoIcon from '~/assets/svg/newSvg/info-icon.svg?react';
import InfoIconGreen from '~/assets/svg/newSvg/info-icon-green.svg?react';
import styles from './TransactionTable.module.scss';
import { FoodOrderWares } from '../TransactionForm';
import { translateFoodTypeItem, translateMeasure } from '~/utils/translateOptions';

interface Props {
  order: FoodOrderWares[];
  selectedItems: number[];
  isAllCancelled: boolean;
  totalSum: number;
  totalAmount: string;
  currency: string;
  onCheckboxHeadPress: () => void;
  onCheckboxPress: (id: number) => void;
}

export const TransactionTable = ({
  order,
  isAllCancelled,
  totalSum,
  selectedItems,
  totalAmount,
  currency,
  onCheckboxPress,
  onCheckboxHeadPress,
}: Props): JSX.Element => {
  const [sort, setSort] = useState({
    field: '',
    direction: '',
  });
  const { t } = useTranslation();

  const getCancelledByTranslated = (user: string) => {
    return user === 'user'
      ? t('by_user')
      : user === 'admin'
        ? t('by_admin')
        : user === 'system'
          ? t('by_system')
          : '';
  };

  const totalFixedSum = totalSum?.toFixed(2) ?? 0.0;

  return (
    <div
      style={
        isAllCancelled ? { backgroundColor: 'rgba(75, 70, 92, 0.08)' } : {}
      }
      className={styles.tableWrapper}
    >
      <Table>
        <TableHead sort={sort} setSort={setSort}>
          <TableCellHead
            className={styles.tableCellHead}
            sortable={false}
            field="checkbox"
          >
            {order?.filter(({ is_cancel }) => !is_cancel).length ? (
              <CustomCheckbox
                isChecked={
                  selectedItems.length === order.length && !!order.length
                }
                onChange={onCheckboxHeadPress}
              />
            ) : (
              ''
            )}
          </TableCellHead>
          <TableCellHead sortable={false} field="name">
            {t('name')}
          </TableCellHead>
          <TableCellHead sortable={false} field="category">
            {t('service')}
          </TableCellHead>
          <TableCellHead sortable={false} field="weight">
            {t('measure')}
          </TableCellHead>
          <TableCellHead sortable={false} field="price">
            {t('price')}
          </TableCellHead>
          <TableCellHead sortable={false} field="amount">
            {t('quantity_full')}
          </TableCellHead>
          <TableCellHead sortable={false} field="sum">
            {t('total_amount')}
          </TableCellHead>
        </TableHead>
        <TableBody>
          {order?.map(
            ({
              food_ware_id,
              is_cancel,
              is_submit,
              cancel_by,
              ware_name,
              ware_qty,
              ware_type,
              ware_price,
              measure,
              weight,
            }: FoodOrderWares) => (
              <TableRow
                style={is_cancel ? styles.transactionRow : ''}
                key={food_ware_id}
              >
                <TableCell className={styles.checkboxCell}>
                  {is_cancel ? (
                    <Tooltip
                      tooltipStyle={styles.tooltipStyle}
                      className={styles.transactionsTooltip}
                      position="right"
                      title={`${t(
                        'dish_cancelled_by'
                      )} ${getCancelledByTranslated(cancel_by)}`}
                    >
                      <InfoIcon className={styles.tooltipIcon} />
                    </Tooltip>
                  ) : is_submit ? (
                    <Tooltip
                      tooltipStyle={styles.tooltipStyle}
                      className={styles.transactionsTooltip}
                      position="right"
                      title={t('confirmed_dish')}
                    >
                      <InfoIconGreen
                        color="green"
                        className={styles.tooltipIconGreen}
                      />
                    </Tooltip>
                  ) : (
                    <CustomCheckbox
                      isChecked={selectedItems.includes(food_ware_id)}
                      onChange={() => onCheckboxPress(food_ware_id)}
                    />
                  )}
                </TableCell>
                <TableCell>{ware_name}</TableCell>
                <TableCell>
                  {(translateFoodTypeItem({ id: 0, name: ware_type } as any, t) as any)?.display_name ?? ware_type}
                </TableCell>
                <TableCell>
                  {weight.split(' ')[0]}
                  {translateMeasure(measure, t)}
                </TableCell>
                <TableCell>
                  {ware_price}
                  {currency}
                </TableCell>
                <TableCell>{ware_qty}</TableCell>
                <TableCell>
                  {Number(ware_price) * Number(ware_qty)}{currency}
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
        <TableRow>
          <TableCell>
            <span className={styles.boldText}>{t('in_total')}:</span>
          </TableCell>
          <TableCell>&nbsp;</TableCell>
          <TableCell>&nbsp;</TableCell>
          <TableCell>&nbsp;</TableCell>
          <TableCell>&nbsp;</TableCell>
          <TableCell>
            <span className={styles.boldText}>{totalAmount}</span>
          </TableCell>
          <TableCell>
            <span className={styles.boldText}>
              {totalFixedSum}
              {currency}
            </span>
          </TableCell>
        </TableRow>
      </Table>
    </div>
  );
};
